import { useState } from 'react';
import { Plus, Search, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ReguaModal } from '@/components/reguas/ReguaModal';
import { CobrancaModal } from '@/components/reguas/CobrancaModal';
import { StrategyLibraryModal } from '@/components/reguas/StrategyLibraryModal';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { ReguaRow } from '@/components/reguas/ReguaRow';
import type { CollectionStep } from '@/components/reguas/ReguaTimeline';

export interface CollectionRule {
  id: string;
  name: string;
  trigger_days: number;
  channel: 'whatsapp' | 'email' | 'sms';
  is_active: boolean | null;
  apply_on_due_soon: boolean | null;
  apply_on_overdue: boolean | null;
  min_amount: number | null;
  max_amount: number | null;
  company_id: string;
  // Original schema fields
  idade_min: number | null;
  idade_max: number | null;
  menos_que: boolean | null;
  dias_atraso: number | null;
  estado: string | null;
  cidade: string | null;
  bairro: string | null;
  prioridade: number | null;
}


const ReguaCobranca = () => {
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState<CollectionRule | null>(null);
  const [deleteRuleId, setDeleteRuleId] = useState<string | null>(null);
  const [stepModalOpen, setStepModalOpen] = useState(false);
  const [selectedRuleForStep, setSelectedRuleForStep] = useState<string | null>(null);
  const [selectedStep, setSelectedStep] = useState<CollectionStep | null>(null);
  const [strategyModalOpen, setStrategyModalOpen] = useState(false);
  const queryClient = useQueryClient();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const { data: rules = [], isLoading } = useQuery({
    queryKey: ['collection-rules'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.company_id) return [];

      const { data, error } = await supabase
        .from('collection_rules')
        .select('*')
        .eq('company_id', profile.company_id)
        .order('prioridade', { ascending: false });

      if (error) throw error;
      return data as CollectionRule[];
    }
  });

  // Fetch customer counts per rule
  const { data: customerCounts = {} } = useQuery({
    queryKey: ['customer-counts-by-rule'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return {};

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.company_id) return {};

      const { data, error } = await supabase
        .from('customers')
        .select('collection_rule_id')
        .eq('company_id', profile.company_id);

      if (error) throw error;

      // Group and count
      const counts: Record<string, number> = {};
      (data || []).forEach(customer => {
        if (customer.collection_rule_id) {
          counts[customer.collection_rule_id] = (counts[customer.collection_rule_id] || 0) + 1;
        }
      });
      return counts;
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('collection_rules')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collection-rules'] });
      setDeleteRuleId(null);
    }
  });

  const reorderMutation = useMutation({
    mutationFn: async (orderedIds: string[]) => {
      // Update prioridade based on new order (higher = first)
      const updates = orderedIds.map((id, index) => 
        supabase
          .from('collection_rules')
          .update({ prioridade: orderedIds.length - index })
          .eq('id', id)
      );
      await Promise.all(updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collection-rules'] });
    }
  });

  const filtered = rules.filter(r => {
    if (!search.trim()) return true;
    return r.name.toLowerCase().includes(search.toLowerCase());
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = filtered.findIndex((r) => r.id === active.id);
      const newIndex = filtered.findIndex((r) => r.id === over.id);
      const newOrder = arrayMove(filtered, oldIndex, newIndex);
      reorderMutation.mutate(newOrder.map(r => r.id));
    }
  };

  const handleEdit = (rule: CollectionRule) => {
    setSelectedRule(rule);
    setModalOpen(true);
  };

  const handleCreate = () => {
    setSelectedRule(null);
    setModalOpen(true);
  };

  const handleAddStep = (ruleId: string) => {
    setSelectedRuleForStep(ruleId);
    setSelectedStep(null);
    setStepModalOpen(true);
  };

  const handleEditStep = (ruleId: string, step: CollectionStep) => {
    setSelectedRuleForStep(ruleId);
    setSelectedStep(step);
    setStepModalOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Réguas de Cobrança</h1>
          <p className="text-muted-foreground">Configure as etapas de cobrança automática</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => setStrategyModalOpen(true)} 
            className="gap-2 hover-pop border-[hsl(var(--accent-300))] text-[hsl(var(--accent-600))] hover:bg-[hsl(var(--accent-50))]"
          >
            <BookOpen className="h-4 w-4" />
            Estratégias
          </Button>
          <Button onClick={handleCreate} className="gap-2 hover-pop">
            <Plus className="h-4 w-4" />
            Nova Régua
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por nome..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Lista de Réguas */}
      <div className="bg-card rounded-lg border overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-4 py-3 px-4 bg-muted/50 border-b font-medium text-xs text-muted-foreground uppercase tracking-wide">
          <span className="w-5"></span>
          <span className="min-w-[120px]">Nome da Régua</span>
          <span className="w-24 text-center">Clientes</span>
          <span className="flex-1 text-center">Critério</span>
          <span className="w-[120px] text-right">Ações</span>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="p-8 text-center text-muted-foreground">
            Carregando...
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            {search ? 'Nenhuma régua encontrada.' : 'Nenhuma régua configurada. Clique em "Nova Régua" para criar uma.'}
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={filtered.map(r => r.id)} strategy={verticalListSortingStrategy}>
              <div className="divide-y">
                {filtered.map((rule) => (
                  <ReguaRow
                    key={rule.id}
                    rule={rule}
                    customerCount={customerCounts[rule.id] || 0}
                    onEdit={() => handleEdit(rule)}
                    onDelete={() => setDeleteRuleId(rule.id)}
                    onAddStep={() => handleAddStep(rule.id)}
                    onEditStep={(step) => handleEditStep(rule.id, step)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      <ReguaModal 
        open={modalOpen}
        onOpenChange={setModalOpen}
        rule={selectedRule}
        onSaved={() => queryClient.invalidateQueries({ queryKey: ['collection-rules'] })}
      />

      {selectedRuleForStep && (
        <CobrancaModal
          open={stepModalOpen}
          onOpenChange={setStepModalOpen}
          ruleId={selectedRuleForStep}
          step={selectedStep}
          onSaved={() => queryClient.invalidateQueries({ queryKey: ['collection-steps', selectedRuleForStep] })}
        />
      )}

      <StrategyLibraryModal
        open={strategyModalOpen}
        onOpenChange={setStrategyModalOpen}
      />

      <ConfirmDialog
        open={!!deleteRuleId}
        onOpenChange={(open) => !open && setDeleteRuleId(null)}
        title="Excluir Régua"
        description="Tem certeza que deseja excluir esta régua? Esta ação não pode ser desfeita."
        onConfirm={() => deleteRuleId && deleteMutation.mutate(deleteRuleId)}
        confirmText="Excluir"
        variant="destructive"
      />
    </div>
  );
};

export default ReguaCobranca;
